import { useCallback, useEffect, useRef, useState } from 'react';
import type { DragEvent, ReactNode } from 'react';
import { Splitter } from 'antd';
import {
  CheckOutlined,
  DeleteOutlined,
  HolderOutlined,
  InfoCircleOutlined,
  PlusOutlined,
  ProfileOutlined,
  ReloadOutlined,
  WarningOutlined,
} from '@ant-design/icons';

import { Button, Checkbox, Dialog, Field, Input, Select, Textarea } from '../../../components/ui';
import { useT } from '../../../i18n';
import { cn } from '../../../lib/cn';
import { loiTheoTruong } from '../../../lib/errors';
import { NHAN_KIEU_TRUONG } from '../constants';
import { phanDau, phanKy } from '../nghiep-vu/khung-van-ban';
import {
  THE_THUC_RONG,
  TRUONG_RONG,
  dungTruong,
  sangTheThucMau,
  sinhKhoa,
  thoatHtml,
  tuTheThucMau,
  tuTruongMau,
} from '../nghiep-vu/mau-van-ban';
import type { TheThucNhap, TruongNhap } from '../nghiep-vu/mau-van-ban';
import type { GhiMau, KieuTruong, MauVanBan, TrangThaiMau } from '../types';
import { LoiForm } from './LoiForm';
import { ThanhCongCuVanBan } from './ThanhCongCuVanBan';

// ─── Trình soạn contentEditable (chuyển nguyên từ MauVanBanFormDialog.tsx của foxainative) ─────────

function chip(ten: string): string {
  const n = ten.trim();
  return `<span class="tplfld" contenteditable="false" data-fld="${thoatHtml(n)}">${thoatHtml(n)}</span>`;
}

/** Ký tự ngay trước con trỏ: '' là đầu khối; ' ' là liền sau một phần tử hay chip. */
function kyTuTruocConTro(range: Range): string {
  const node = range.startContainer;
  const off = range.startOffset;
  if (node.nodeType === Node.TEXT_NODE) {
    if (off > 0) return (node.textContent ?? '').charAt(off - 1);
    const prev = node.previousSibling;
    if (!prev) return '';
    return prev.nodeType === Node.TEXT_NODE ? (prev.textContent ?? '').slice(-1) : ' ';
  }
  const child = node.childNodes[off - 1];
  if (!child) return '';
  return child.nodeType === Node.TEXT_NODE ? (child.textContent ?? '').slice(-1) : ' ';
}

function vungSoanCua(node: Node | null): HTMLElement | null {
  let el: HTMLElement | null = node instanceof HTMLElement ? node : (node?.parentElement ?? null);
  while (el && !el.isContentEditable) el = el.parentElement;
  return el;
}

/**
 * Chèn chip tại con trỏ, thêm khoảng trắng trước nếu cần. Chèn node DOM trực tiếp (không `insertHTML`) để
 * chip luôn nằm cùng dòng: Chrome tự ngắt khối khi chèn span `contenteditable=false` lúc vùng soạn vừa mất
 * focus.
 */
function chenChip(ten: string, range: Range): void {
  vungSoanCua(range.startContainer)?.focus();
  const nm = ten.trim();
  if (!nm) return;
  const el = document.createElement('span');
  el.className = 'tplfld';
  el.setAttribute('contenteditable', 'false');
  el.setAttribute('data-fld', nm);
  el.textContent = nm;
  const c = kyTuTruocConTro(range);
  const canDau = c !== '' && !/[\s ]/.test(c);
  // insertNode chèn ở đầu range nên chèn ngược thứ tự: [cách?][chip][cách]
  range.deleteContents();
  const sau = document.createTextNode(' ');
  range.insertNode(sau);
  range.insertNode(el);
  if (canDau) range.insertNode(document.createTextNode(' '));
  const sel = window.getSelection();
  if (sel) {
    const r = document.createRange();
    r.setStartAfter(sau);
    r.collapse(true);
    sel.removeAllRanges();
    sel.addRange(r);
  }
}

function thanGoiY(goiY: string): string {
  return `<p data-ph="1" style="margin:6px 0;text-align:justify;">${thoatHtml(goiY)}</p>`;
}

function thanTuTruong(ds: TruongNhap[], goiY: string): string {
  const coTen = ds.filter((f) => f.name.trim());
  if (!coTen.length) return thanGoiY(goiY);
  return coTen
    .map((f) => `<p style="margin:6px 0;text-align:justify;">${thoatHtml(f.name.trim())}: ${chip(f.name)}&nbsp;</p>`)
    .join('');
}

/** Thân vùng soạn → `body_template` có `{{key}}`; '' nếu chưa soạn gì. */
function thanSangMau(el: HTMLElement | null, khoaTheoNhan: Record<string, string>, goiY: string): string {
  if (!el) return '';
  const clone = el.cloneNode(true) as HTMLElement;
  // Chỉ bỏ đoạn gợi ý khi người dùng chưa sửa nó
  clone.querySelectorAll('[data-ph]').forEach((n) => {
    if ((n.textContent ?? '').replace(/\s+/g, ' ').trim() === goiY) n.remove();
  });
  clone.querySelectorAll('[data-fld]').forEach((sp) => {
    const ten = (sp.getAttribute('data-fld') ?? '').trim();
    sp.replaceWith(`{{${khoaTheoNhan[ten] ?? sinhKhoa(ten)}}}`);
  });
  if (!(clone.textContent ?? '').replace(/\s+/g, ' ').trim()) return '';
  return clone.innerHTML.trim();
}

/** `body_template` → HTML cho vùng soạn, đổi `{{key}}` về chip theo nhãn. */
function mauSangThan(than: string, nhanTheoKhoa: Record<string, string>, goiY: string): string {
  const html = than.replace(/\{\{\s*(\w+)\s*\}\}/g, (_m, k: string) => chip(nhanTheoKhoa[k] ?? k));
  return html.trim() || thanGoiY(goiY);
}

/** Gỡ khung thể thức mà mẫu đời trước từng "nướng" vào `body_template`; khung giờ dựng từ `default_meta`. */
function goKhungCu(html: string): string {
  if (!html) return html;
  const div = document.createElement('div');
  div.innerHTML = html;
  div.querySelectorAll('table').forEach((t) => {
    if (/CỘNG HÒA XÃ HỘI|Nơi nhận|\(Đã ký\)/i.test(t.textContent ?? '')) t.remove();
  });
  div.querySelectorAll('div').forEach((d) => {
    if ((d.getAttribute('style') ?? '').includes('margin:18px 0 12px')) d.remove();
  });
  return div.innerHTML.trim();
}

type DocCoCaret = { caretRangeFromPoint?(x: number, y: number): Range | null };

function rangeTaiDiem(x: number, y: number): Range | null {
  const doc = document as Document & DocCoCaret;
  if (typeof doc.caretRangeFromPoint === 'function') return doc.caretRangeFromPoint(x, y);
  if (typeof doc.caretPositionFromPoint === 'function') {
    const pos = doc.caretPositionFromPoint(x, y);
    if (pos) {
      const r = document.createRange();
      r.setStart(pos.offsetNode, pos.offset);
      r.collapse(true);
      return r;
    }
  }
  return null;
}

function NhanMuc({ children, className }: { children: ReactNode; className?: string }) {
  return (
    <div className={cn('text-2xs font-semibold uppercase tracking-wide text-content-secondary', className)}>
      {children}
    </div>
  );
}

// ─── Hộp thoại ───────────────────────────────────────────────────────────────

interface ThongTin {
  name: string;
  code: string;
  desc: string;
  active: boolean;
}

export function MauVanBanFormDialog({
  open,
  mau,
  saving,
  loiMayChu,
  onClose,
  onSubmit,
}: {
  open: boolean;
  /** Có giá trị là đang sửa. */
  mau: MauVanBan | null;
  saving: boolean;
  loiMayChu: unknown;
  onClose: () => void;
  onSubmit: (vao: GhiMau) => void;
}) {
  const { t } = useT();
  const goiY = t('vanBanMauForm.goiYThan');
  const laNd30 = mau?.source === 'NGHI_DINH_30';

  const [info, setInfo] = useState<ThongTin>({ name: '', code: '', desc: '', active: true });
  const [truong, setTruong] = useState<TruongNhap[]>([]);
  const [theThuc, setTheThuc] = useState<TheThucNhap>(THE_THUC_RONG);
  const [daChinh, setDaChinh] = useState(false);
  const [loiTen, setLoiTen] = useState<string>();

  const boRef = useRef<HTMLDivElement>(null);
  const dauRef = useRef<HTMLDivElement>(null);
  const thanRef = useRef<HTMLDivElement>(null);
  const kyRef = useRef<HTMLDivElement>(null);
  const dauDaSua = useRef(false);
  const kyDaSua = useRef(false);
  const rangeCuoi = useRef<Range | null>(null);

  // Khởi tạo mỗi lần mở cho một bản ghi; vùng soạn dựng lại vì hộp thoại huỷ nội dung khi đóng.
  useEffect(() => {
    if (!open) return;
    const fieldsGoc = mau?.fields ?? [];
    const ds = fieldsGoc.map(tuTruongMau);
    const tt = tuTheThucMau(mau?.default_meta ?? null);
    const nhanTheoKhoa: Record<string, string> = {};
    fieldsGoc.forEach((f) => {
      nhanTheoKhoa[f.key] = f.label;
    });
    const thanCu = goKhungCu(mau?.body_template ?? '');
    const than = thanCu ? mauSangThan(thanCu, nhanTheoKhoa, goiY) : thanTuTruong(ds, goiY);
    const tenMoi = mau?.name ?? '';
    const maMoi = mau?.symbol_code ?? '';
    setInfo({ name: tenMoi, code: maMoi, desc: mau?.description ?? '', active: (mau?.status ?? 'DANG_DUNG') === 'DANG_DUNG' });
    setTruong(ds);
    setTheThuc(tt);
    setLoiTen(undefined);
    setDaChinh(false);
    dauDaSua.current = false;
    kyDaSua.current = false;
    const id = window.setTimeout(() => {
      if (dauRef.current) dauRef.current.innerHTML = phanDau(tenMoi, maMoi, tt);
      if (thanRef.current) thanRef.current.innerHTML = than;
      if (kyRef.current) kyRef.current.innerHTML = phanKy(tt);
    }, 0);
    return () => window.clearTimeout(id);
    // Chỉ chạy khi mở hoặc đổi bản ghi, không chạy khi gõ tên (sẽ xoá nội dung đang soạn)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, mau?.id]);

  // Phần đầu theo tên, ký hiệu và thể thức, trừ khi người dùng đã sửa tay phần đầu.
  useEffect(() => {
    if (!open || dauDaSua.current || !dauRef.current) return;
    dauRef.current.innerHTML = phanDau(info.name, info.code, theThuc);
  }, [info.name, info.code, theThuc, open]);

  useEffect(() => {
    if (!open || kyDaSua.current || !kyRef.current) return;
    kyRef.current.innerHTML = phanKy(theThuc);
  }, [theThuc, open]);

  // Nhớ vị trí con trỏ cuối cùng trong vùng soạn, để bấm chip vẫn chèn đúng chỗ.
  useEffect(() => {
    if (!open) return;
    const nghe = () => {
      const bo = boRef.current;
      const sel = window.getSelection();
      if (!bo || !sel || !sel.rangeCount) return;
      const r = sel.getRangeAt(0);
      if (bo.contains(r.startContainer)) rangeCuoi.current = r.cloneRange();
    };
    document.addEventListener('selectionchange', nghe);
    return () => document.removeEventListener('selectionchange', nghe);
  }, [open]);

  // Lỗi `#/name` của backend gắn vào ô tên; lỗi khác hiện ở đầu hộp thoại.
  useEffect(() => {
    const m = loiTheoTruong(loiMayChu);
    if (m['/name']) setLoiTen(m['/name']);
  }, [loiMayChu]);

  const dungLai = useCallback(() => {
    if (dauRef.current) dauRef.current.innerHTML = phanDau(info.name, info.code, theThuc);
    if (thanRef.current) thanRef.current.innerHTML = thanTuTruong(truong, goiY);
    if (kyRef.current) kyRef.current.innerHTML = phanKy(theThuc);
    dauDaSua.current = false;
    kyDaSua.current = false;
    setDaChinh(false);
  }, [info.name, info.code, theThuc, truong, goiY]);

  const rangeChen = useCallback((): Range | null => {
    const bo = boRef.current;
    if (!bo) return null;
    const sel = window.getSelection();
    if (sel && sel.rangeCount && bo.contains(sel.getRangeAt(0).startContainer)) return sel.getRangeAt(0);
    if (rangeCuoi.current && bo.contains(rangeCuoi.current.startContainer)) return rangeCuoi.current;
    const than = thanRef.current;
    if (!than) return null;
    than.focus();
    const r = document.createRange();
    r.selectNodeContents(than.lastElementChild ?? than);
    r.collapse(false);
    return r;
  }, []);

  const chenTruong = useCallback(
    (ten: string) => {
      const r = rangeChen();
      if (!r || !ten.trim()) return;
      chenChip(ten, r);
      setDaChinh(true);
    },
    [rangeChen],
  );

  const thaTruong = useCallback((e: DragEvent) => {
    e.preventDefault();
    const bo = boRef.current;
    bo?.classList.remove('drop-hot');
    const ten = (e.dataTransfer.getData('text/plain') || '').trim();
    if (!ten || !bo) return;
    let r = rangeTaiDiem(e.clientX, e.clientY);
    if (!r || !bo.contains(r.startContainer)) {
      const than = thanRef.current;
      if (!than) return;
      r = document.createRange();
      r.selectNodeContents(than.lastElementChild ?? than);
      r.collapse(false);
    }
    chenChip(ten, r);
    setDaChinh(true);
  }, []);

  const suaTruong = (i: number, p: Partial<TruongNhap>) =>
    setTruong((ds) => ds.map((f, j) => (j === i ? { ...f, ...p } : f)));
  const suaTheThuc = (p: Partial<TheThucNhap>) => setTheThuc((f) => ({ ...f, ...p }));
  const coTen = truong.filter((f) => f.name.trim());

  const luu = () => {
    if (!info.name.trim()) {
      setLoiTen(t('vanBanMauForm.loiTenTrong'));
      return;
    }
    const { fields, khoaTheoNhan } = dungTruong(truong);
    const vao: GhiMau = {
      name: info.name.trim(),
      symbol_code: info.code.trim(),
      status: (info.active ? 'DANG_DUNG' : 'NGUNG_DUNG') satisfies TrangThaiMau,
      description: info.desc.trim() || undefined,
      default_meta: sangTheThucMau(theThuc),
    };
    // Mẫu NĐ30 chỉ sửa mô tả và thể thức; fields, body_template chỉ áp cho mẫu tuỳ chỉnh.
    // body_template chỉ lưu thân (có {{key}}); khung thể thức nằm ở default_meta.
    if (!laNd30) {
      vao.fields = fields;
      vao.body_template = thanSangMau(thanRef.current, khoaTheoNhan, goiY);
    }
    onSubmit(vao);
  };

  const kieuOptions = (Object.keys(NHAN_KIEU_TRUONG) as KieuTruong[]).map((value) => ({
    value,
    label: t(NHAN_KIEU_TRUONG[value]),
  }));

  const o = (k: keyof TheThucNhap, nhan: Parameters<typeof t>[0], goi: Parameters<typeof t>[0]) => (
    <Field label={t(nhan)}>
      {(p) => (
        <Input
          {...p}
          placeholder={t(goi)}
          value={theThuc[k] as string}
          onChange={(e) => suaTheThuc({ [k]: e.target.value } as Partial<TheThucNhap>)}
        />
      )}
    </Field>
  );

  return (
    <Dialog
      open={open}
      onClose={onClose}
      width="96vw"
      style={{ maxWidth: 1760, top: 16 }}
      destroyOnHidden
      title={t(mau ? 'vanBanMauForm.dialogSua' : 'vanBanMauForm.dialogThem')}
      footer={
        <div className="flex justify-end gap-3">
          <Button variant="outline" onClick={onClose}>
            {t('chung.huy')}
          </Button>
          <Button variant="primary" icon={<CheckOutlined />} loading={saving} onClick={luu}>
            {t(saving ? 'chung.dangLuu' : mau ? 'vanBanChung.luuThayDoi' : 'vanBanMauForm.nutThem')}
          </Button>
        </div>
      }
    >
      <LoiForm loi={loiMayChu} />
      <Splitter className="mt-2 h-[82vh] max-h-[880px] min-h-[540px]">
        {/* Cột trái: thông tin, thể thức, trường */}
        <Splitter.Panel defaultSize="36%" min="28%" max="52%">
          <div className="flex h-full min-h-0 flex-col gap-4 overflow-y-auto px-1 pr-3">
            <div className="space-y-3.5">
              <NhanMuc>{t('vanBanMauForm.thongTinChung')}</NhanMuc>
              <Field label={t('vanBanMauForm.fTen')} required error={loiTen}>
                {(p) => (
                  <Input
                    {...p}
                    placeholder={t('vanBanMauForm.fTenGoiY')}
                    value={info.name}
                    onChange={(e) => setInfo((f) => ({ ...f, name: e.target.value }))}
                  />
                )}
              </Field>
              <div className="grid grid-cols-2 gap-3">
                <Field label={t('vanBanMauForm.fKyHieu')}>
                  {(p) => (
                    <Input
                      {...p}
                      placeholder={t('vanBanMauForm.fKyHieuGoiY')}
                      value={info.code}
                      onChange={(e) => setInfo((f) => ({ ...f, code: e.target.value }))}
                    />
                  )}
                </Field>
                <Field label={t('vanBanMauForm.fTrangThai')}>
                  {(p) => (
                    <Select
                      {...p}
                      className="w-full"
                      value={info.active ? '1' : '0'}
                      onChange={(v) => setInfo((f) => ({ ...f, active: v === '1' }))}
                      options={[
                        { value: '1', label: t('vanBanMau.trangThai.DANG_DUNG') },
                        { value: '0', label: t('vanBanMau.trangThai.NGUNG_DUNG') },
                      ]}
                    />
                  )}
                </Field>
              </div>
              <Field label={t('vanBanMauForm.fMoTa')}>
                {(p) => (
                  <Textarea
                    {...p}
                    rows={2}
                    placeholder={t('vanBanMauForm.fMoTaGoiY')}
                    value={info.desc}
                    onChange={(e) => setInfo((f) => ({ ...f, desc: e.target.value }))}
                  />
                )}
              </Field>
            </div>

            <div className="space-y-2">
              <NhanMuc>{t('vanBanMauForm.theThuc')}</NhanMuc>
              <p className="text-xs leading-relaxed text-content-muted">{t('vanBanMauForm.theThucMo')}</p>
              <div className="rounded border border-default bg-subtle px-3 py-2">
                <Checkbox
                  checked={theThuc.headerCenter}
                  onChange={(e) => suaTheThuc({ headerCenter: e.target.checked })}
                >
                  <span className="text-sm text-content-secondary">
                    {t('vanBanMauForm.canGiua')}{' '}
                    <span className="text-xs text-content-muted">{t('vanBanMauForm.canGiuaMo')}</span>
                  </span>
                </Checkbox>
              </div>
              <div className="grid grid-cols-2 gap-2.5 rounded-lg border border-default bg-subtle p-3">
                {o('cqch', 'vanBanMauForm.fCqch', 'vanBanMauForm.fCqchGoiY')}
                {o('cq', 'vanBanMauForm.fCq', 'vanBanMauForm.fCqGoiY')}
                {o('so', 'vanBanMauForm.fSo', 'vanBanMauForm.fSoGoiY')}
                {o('kyhieu', 'vanBanMauForm.fKyHieuCq', 'vanBanMauForm.fKyHieuCqGoiY')}
                {o('dd', 'vanBanMauForm.fDd', 'vanBanMauForm.fDdGoiY')}
                {o('cv', 'vanBanMauForm.fCv', 'vanBanMauForm.fCvGoiY')}
                <div className="col-span-2">{o('ky', 'vanBanMauForm.fKy', 'vanBanMauForm.fKyGoiY')}</div>
                <div className="col-span-2">
                  <Field label={t('vanBanMauForm.fNn')}>
                    {(p) => (
                      <Textarea
                        {...p}
                        rows={3}
                        placeholder={t('vanBanMauForm.fNnGoiY')}
                        value={theThuc.nn}
                        onChange={(e) => suaTheThuc({ nn: e.target.value })}
                      />
                    )}
                  </Field>
                </div>
              </div>
            </div>

            <div className="flex flex-col rounded-lg border border-default bg-subtle">
              <div className="flex items-start justify-between gap-2 border-b border-default px-3.5 py-3">
                <div className="min-w-0">
                  <NhanMuc className="mb-0.5">{t('vanBanMauForm.truong')}</NhanMuc>
                  <p className="text-xs leading-relaxed text-content-muted">{t('vanBanMauForm.truongMo')}</p>
                </div>
                <Button
                  variant="primary"
                  size="sm"
                  icon={<PlusOutlined />}
                  className="shrink-0"
                  onClick={() => setTruong((ds) => [...ds, { ...TRUONG_RONG }])}
                >
                  {t('vanBanMauForm.themTruong')}
                </Button>
              </div>
              <div className="p-3">
                {truong.length === 0 ? (
                  <div className="flex flex-col items-center justify-center gap-2 rounded border border-dashed border-default px-4 py-10 text-center">
                    <ProfileOutlined className="text-2xl text-content-muted" />
                    <p className="text-xs text-content-muted">
                      {t('vanBanMauForm.chuaCoTruong')}
                      <br />
                      {t('vanBanMauForm.chuaCoTruongMo')}
                    </p>
                  </div>
                ) : (
                  <div className="space-y-2">
                    {truong.map((f, i) => (
                      <div key={i} className="rounded border border-default bg-surface p-2.5 shadow-sm">
                        <div className="mb-2 flex items-center gap-2">
                          <span className="flex size-5 shrink-0 items-center justify-center rounded-full bg-primary-50 text-2xs font-semibold text-primary-700">
                            {i + 1}
                          </span>
                          <Input
                            className="flex-1"
                            aria-label={t('vanBanMauForm.fTenTruong', { so: i + 1 })}
                            placeholder={t('vanBanMauForm.fTenTruongGoiY')}
                            value={f.name}
                            onChange={(e) => suaTruong(i, { name: e.target.value })}
                          />
                          <Button
                            variant="ghost"
                            size="sm"
                            danger
                            icon={<DeleteOutlined />}
                            title={t('vanBanMauForm.xoaTruong', { so: i + 1 })}
                            aria-label={t('vanBanMauForm.xoaTruong', { so: i + 1 })}
                            className="shrink-0"
                            onClick={() => setTruong((ds) => ds.filter((_, j) => j !== i))}
                          />
                        </div>
                        <div className="grid grid-cols-[minmax(0,140px)_minmax(0,1fr)] gap-2 pl-7">
                          <Select
                            size="small"
                            className="w-full"
                            aria-label={t('vanBanMauForm.fKieuTruong', { so: i + 1 })}
                            options={kieuOptions}
                            value={f.type}
                            onChange={(v) => suaTruong(i, { type: v as KieuTruong })}
                          />
                          <Input
                            size="small"
                            aria-label={t('vanBanMauForm.fGoiYAi')}
                            placeholder={t('vanBanMauForm.fGoiYAi')}
                            value={f.note}
                            onChange={(e) => suaTruong(i, { note: e.target.value })}
                          />
                        </div>
                        {f.type === 'SELECT' && (
                          <div className="mt-2 pl-7">
                            <Input
                              size="small"
                              aria-label={t('vanBanMauForm.fLuaChon')}
                              placeholder={t('vanBanMauForm.fLuaChon')}
                              value={f.options.join(', ')}
                              onChange={(e) => suaTruong(i, { options: e.target.value.split(',').map((x) => x.trim()) })}
                            />
                          </div>
                        )}
                        <div className="mt-2 flex flex-wrap items-center gap-x-4 gap-y-1.5 pl-7">
                          <Checkbox checked={f.required} onChange={(e) => suaTruong(i, { required: e.target.checked })}>
                            <span className="text-xs text-content-secondary">{t('vanBanMauForm.batBuoc')}</span>
                          </Checkbox>
                          <span title={t('vanBanMauForm.aiTuDienMo')}>
                            <Checkbox checked={f.aiFill} onChange={(e) => suaTruong(i, { aiFill: e.target.checked })}>
                              <span className="text-xs text-content-secondary">{t('vanBanMauForm.aiTuDien')}</span>
                            </Checkbox>
                          </span>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
                {laNd30 && (
                  <div className="mt-3 flex items-start gap-2 rounded border border-primary-200 bg-primary-50 px-3 py-2 text-xs text-primary-700">
                    <InfoCircleOutlined className="mt-0.5 shrink-0" />
                    {t('vanBanMauForm.luuYNd30')}
                  </div>
                )}
              </div>
            </div>
          </div>
        </Splitter.Panel>

        {/* Cột phải: bản xem trước soạn như Word */}
        <Splitter.Panel>
          <div className="flex h-full min-h-0 flex-col gap-2 px-1 pl-3">
            <div className="flex items-center justify-between gap-2">
              <NhanMuc>{t('vanBanMauForm.xemTruoc')}</NhanMuc>
              {daChinh && (
                <span className="inline-flex items-center gap-1.5 text-xs text-warning-700">
                  <WarningOutlined /> {t('vanBanMauForm.daChinhTay')}
                  <Button variant="ghost" size="sm" icon={<ReloadOutlined />} title={t('vanBanMauForm.dungLaiMo')} onClick={dungLai}>
                    {t('vanBanMauForm.dungLai')}
                  </Button>
                </span>
              )}
            </div>

            <div className="flex min-h-0 flex-1 flex-col overflow-hidden rounded-lg border border-default">
              <div className="flex flex-wrap items-center gap-1.5 border-b border-default bg-subtle px-3 py-2">
                <span className="mr-1 text-xs font-medium text-content-secondary">{t('vanBanMauForm.chenTruong')}</span>
                {coTen.length === 0 ? (
                  <span className="text-xs text-content-muted">{t('vanBanMauForm.chenTruongRong')}</span>
                ) : (
                  coTen.map((f) => (
                    <button
                      key={f.name}
                      type="button"
                      draggable
                      onDragStart={(e) => {
                        e.dataTransfer.setData('text/plain', f.name.trim());
                        e.dataTransfer.effectAllowed = 'copy';
                      }}
                      onClick={() => chenTruong(f.name)}
                      title={t('vanBanMauForm.chenTruongMo')}
                      className="inline-flex cursor-grab items-center gap-1 rounded border border-primary-200 bg-primary-50 px-2 py-1 text-xs text-primary-700 hover:bg-primary-100"
                    >
                      <HolderOutlined className="text-2xs" /> {f.name.trim()}
                    </button>
                  ))
                )}
              </div>

              <ThanhCongCuVanBan onAfterExec={() => setDaChinh(true)} />

              {/* Mặt bàn và trang giấy */}
              <div className="min-h-0 flex-1 overflow-y-auto bg-page p-5">
                <div
                  ref={boRef}
                  className="mvb-doc mx-auto max-w-[820px] rounded-lg border border-default shadow-md"
                  onDragOver={(e) => {
                    if (Array.prototype.indexOf.call(e.dataTransfer.types, 'text/plain') >= 0) {
                      e.preventDefault();
                      e.dataTransfer.dropEffect = 'copy';
                      boRef.current?.classList.add('drop-hot');
                    }
                  }}
                  onDragLeave={() => boRef.current?.classList.remove('drop-hot')}
                  onDrop={thaTruong}
                >
                  <div
                    ref={dauRef}
                    role="textbox"
                    aria-multiline="true"
                    aria-label={t('vanBanMauForm.vungDau')}
                    contentEditable
                    suppressContentEditableWarning
                    spellCheck={false}
                    onInput={() => {
                      dauDaSua.current = true;
                      setDaChinh(true);
                    }}
                  />
                  <div
                    ref={thanRef}
                    role="textbox"
                    aria-multiline="true"
                    aria-label={t('vanBanMauForm.vungThan')}
                    contentEditable
                    suppressContentEditableWarning
                    spellCheck={false}
                    onInput={() => setDaChinh(true)}
                    className="min-h-[80px]"
                  />
                  <div
                    ref={kyRef}
                    role="textbox"
                    aria-multiline="true"
                    aria-label={t('vanBanMauForm.vungKy')}
                    contentEditable
                    suppressContentEditableWarning
                    spellCheck={false}
                    onInput={() => {
                      kyDaSua.current = true;
                      setDaChinh(true);
                    }}
                  />
                </div>
              </div>
            </div>
          </div>
        </Splitter.Panel>
      </Splitter>
    </Dialog>
  );
}
